import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { MOCK_TRANSACTIONS } from '../data/mockData'
import { getCurrentQuarter, getTaxYear } from '../lib/tax'

const AuthContext = createContext({})
const AppContext  = createContext({})

// ─────────────────────────────────────────
// AUTH PROVIDER
// ─────────────────────────────────────────
export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) loadProfile(session.user.id)
      else setLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setUser(session?.user ?? null)
      if (session?.user) loadProfile(session.user.id)
      else { setProfile(null); setLoading(false) }
    })
    return () => subscription.unsubscribe()
  }, [])

  async function loadProfile(uid) {
    const { data } = await supabase.from('profiles').select('*').eq('id', uid).single()
    setProfile(data)
    setLoading(false)
  }

  const signOut = () => supabase.auth.signOut()

  async function saveProfile(updates) {
    const { data } = await supabase
      .from('profiles')
      .upsert({ id: user.id, ...updates, updated_at: new Date().toISOString() })
      .select().single()
    setProfile(data)
    return data
  }

  return (
    <AuthContext.Provider value={{ user, profile, loading, signOut, saveProfile, loadProfile }}>
      {children}
    </AuthContext.Provider>
  )
}

// ─────────────────────────────────────────
// APP PROVIDER — all data wired to Supabase
// ─────────────────────────────────────────
export function AppProvider({ children }) {
  const { user, profile } = useAuth()

  // ── State ──
  const [transactions,     setTransactions]     = useState([])
  const [mileageTrips,     setMileageTrips]      = useState([])
  const [homeOffice,       setHomeOfficeState]   = useState({ months: 0, hoursPerMonth: 0 })
  const [lockedPeriods,    setLockedPeriods]     = useState([])
  const [categoryHistory,  setCategoryHistory]   = useState([])
  const [merchantRules,    setMerchantRules]     = useState({})
  const [loading,          setLoading]           = useState(true)
  const [syncing,          setSyncing]           = useState(false)
  const [coaNudgeDone,     setCoaNudgeDone]      = useState(false)

  const isDemo = !profile?.bank_connected

  // ── Load all data on mount ──
  useEffect(() => {
    if (!user) return
    loadAll()
  }, [user?.id])

  async function loadAll() {
    setLoading(true)
    await Promise.all([
      loadTransactions(),
      loadMileage(),
      loadHomeOffice(),
      loadSubmissions(),
      loadMerchantRules(),
    ])
    setLoading(false)
  }

  // ─────────────────────────────────────────
  // TRANSACTIONS
  // ─────────────────────────────────────────
  async function loadTransactions() {
    const { data, error } = await supabase
      .from('transactions')
      .select('*')
      .eq('user_id', user.id)
      .order('date', { ascending: false })

    if (error) { console.error('loadTransactions:', error); return }

    if (data && data.length > 0) {
      // Real data exists — use it, normalise field names
      setTransactions(data.map(normaliseTransaction))
    } else {
      // No real data yet — show demo mock data with flag
      setTransactions(
        MOCK_TRANSACTIONS.map(t => ({
          ...t,
          amount: Math.abs(t.amount),
          _isDemo: true,
        }))
      )
    }
  }

  // Normalise DB columns to app field names
  function normaliseTransaction(t) {
    return {
      ...t,
      has_vat_receipt: t.has_receipt,         // DB uses has_receipt
      categorisedAt:   t.categorised_at,
      categorisedBy:   t.categorised_by,
    }
  }

  // Save a single transaction to Supabase
  async function saveTxToDb(tx) {
    const { _isDemo, has_vat_receipt, categorisedAt, categorisedBy, ...rest } = tx
    const row = {
      ...rest,
      user_id:          user.id,
      has_receipt:      has_vat_receipt ?? false,
      categorised_at:   categorisedAt,
      categorised_by:   categorisedBy,
      tax_year:         getTaxYear(tx.date),
      quarter:          getQuarterFromDate(tx.date),
      updated_at:       new Date().toISOString(),
    }
    // Remove mock integer IDs before saving
    if (typeof row.id === 'number') delete row.id

    const { data, error } = await supabase
      .from('transactions')
      .upsert(row, { onConflict: 'id' })
      .select().single()

    if (error) console.error('saveTxToDb:', error)
    return data
  }

  // Categorise a transaction — saves to DB + updates audit log
  const categoriseTx = useCallback(async (id, categoryId, source = 'manual') => {
    const ts = new Date().toISOString()

    // Optimistic update
    setTransactions(prev => prev.map(t => {
      if (t.id !== id) return t
      return { ...t, category: categoryId, categorisedAt: ts, categorisedBy: source }
    }))

    // Append audit log locally
    setCategoryHistory(prev => {
      const existing = prev.filter(h => h.txId !== id)
      return [...existing, { txId: id, categoryId, source, ts }]
    })

    // Persist to Supabase (skip for demo transactions)
    const tx = transactions.find(t => t.id === id)
    if (tx && !tx._isDemo) {
      const { error: txErr } = await supabase
        .from('transactions')
        .update({
          category:        categoryId,
          categorised_at:  ts,
          categorised_by:  source,
          updated_at:      ts,
        })
        .eq('id', id)
        .eq('user_id', user.id)

      if (!txErr) {
        // Write audit log
        await supabase.from('transaction_audit').insert({
          user_id:        user.id,
          transaction_id: id,
          action:         'categorised',
          new_value:      categoryId,
          source,
        })

        // Update merchant rule (learned pattern)
        if (tx.merchant) {
          await supabase.from('merchant_rules').upsert({
            user_id:       user.id,
            merchant:      tx.merchant,
            category:      categoryId,
            times_applied: (merchantRules[tx.merchant]?.times_applied || 0) + 1,
            updated_at:    ts,
          }, { onConflict: 'user_id,merchant' })
        }
      }
    }
  }, [transactions, user, merchantRules])

  // Undo last categorisation
  const undoCategorise = useCallback(async (id) => {
    const log = categoryHistory.filter(h => h.txId === id)
    const prevCategory = log.length >= 2 ? log[log.length - 2].categoryId : null
    await categoriseTx(id, prevCategory, 'undo')
  }, [categoryHistory, categoriseTx])

  // Add a new transaction manually
  async function addTransaction(tx) {
    const newTx = {
      ...tx,
      user_id:   user.id,
      tax_year:  getTaxYear(tx.date),
      quarter:   getQuarterFromDate(tx.date),
      source:    'manual',
    }
    const { data, error } = await supabase
      .from('transactions')
      .insert(newTx)
      .select().single()

    if (error) { console.error('addTransaction:', error); return }
    setTransactions(prev => [normaliseTransaction(data), ...prev])
    return data
  }

  // Upload receipt — saves to Supabase Storage + updates transaction
  async function uploadReceipt(txId, file) {
    const path = `${user.id}/${txId}/${file.name}`

    const { error: uploadErr } = await supabase.storage
      .from('receipts')
      .upload(path, file, { upsert: true })

    if (uploadErr) { console.error('uploadReceipt:', uploadErr); return }

    const { data: { publicUrl } } = supabase.storage.from('receipts').getPublicUrl(path)

    // Update transaction
    await supabase.from('transactions').update({
      has_receipt:          true,
      receipt_url:          path,
      receipt_uploaded_at:  new Date().toISOString(),
    }).eq('id', txId).eq('user_id', user.id)

    // Save receipt record
    await supabase.from('receipts').insert({
      user_id:        user.id,
      transaction_id: txId,
      file_name:      file.name,
      storage_path:   path,
      file_size:      file.size,
      mime_type:      file.type,
    })

    setTransactions(prev => prev.map(t =>
      t.id === txId ? { ...t, has_vat_receipt: true, receipt_url: path } : t
    ))
  }

  // ─────────────────────────────────────────
  // MILEAGE
  // ─────────────────────────────────────────
  async function loadMileage() {
    const { data } = await supabase
      .from('mileage_trips')
      .select('*')
      .eq('user_id', user.id)
      .order('date', { ascending: false })
    if (data) setMileageTrips(data)
  }

  async function addTrip(trip) {
    const taxYear = getTaxYear(trip.date)
    const quarter = getQuarterFromDate(trip.date)
    const allowance = calcMileageAllowance(Number(trip.miles))

    const { data, error } = await supabase
      .from('mileage_trips')
      .insert({ ...trip, user_id: user.id, tax_year: taxYear, quarter, allowance })
      .select().single()

    if (error) { console.error('addTrip:', error); return }
    setMileageTrips(prev => [data, ...prev])
    return data
  }

  async function removeTrip(id) {
    await supabase.from('mileage_trips').delete().eq('id', id).eq('user_id', user.id)
    setMileageTrips(prev => prev.filter(t => t.id !== id))
  }

  // ─────────────────────────────────────────
  // HOME OFFICE
  // ─────────────────────────────────────────
  async function loadHomeOffice() {
    const taxYear = getTaxYear(new Date().toISOString())
    const { data } = await supabase
      .from('home_office')
      .select('*')
      .eq('user_id', user.id)
      .eq('tax_year', taxYear)
      .single()

    if (data) {
      setHomeOfficeState({ months: data.months, hoursPerMonth: data.hours_per_month })
    }
  }

  async function setHomeOffice(updates) {
    const taxYear = getTaxYear(new Date().toISOString())
    setHomeOfficeState(updates)
    await supabase.from('home_office').upsert({
      user_id:        user.id,
      tax_year:       taxYear,
      months:         updates.months,
      hours_per_month: updates.hoursPerMonth,
      updated_at:     new Date().toISOString(),
    }, { onConflict: 'user_id,tax_year' })
  }

  // ─────────────────────────────────────────
  // SUBMISSIONS
  // ─────────────────────────────────────────
  async function loadSubmissions() {
    const { data } = await supabase
      .from('submissions')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })

    if (data) {
      const locked = data
        .filter(s => s.status === 'submitted' || s.status === 'accepted')
        .map(s => s.quarter)
      setLockedPeriods(locked)
    }
  }

  async function saveSubmission(submission) {
    const { data, error } = await supabase
      .from('submissions')
      .upsert({
        ...submission,
        user_id:    user.id,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'user_id,tax_year,quarter' })
      .select().single()

    if (error) { console.error('saveSubmission:', error); return }

    // Lock the quarter
    if (submission.status === 'submitted') {
      lockPeriod(submission.quarter)
      // Lock all transactions in this quarter
      await supabase
        .from('transactions')
        .update({ is_locked: true })
        .eq('user_id', user.id)
        .eq('quarter', submission.quarter)
        .eq('tax_year', submission.tax_year)
    }

    return data
  }

  // ─────────────────────────────────────────
  // AUTO-CATEGORISE — fires after bank connect
  // Uses merchant memory first, then AI for unknowns
  // ─────────────────────────────────────────
  async function autoCategoriseOnConnect() {
    const { autoCategoriseBatch } = await import('../lib/claudeAI')
    const uncategorised = transactions.filter(t => !t.category && !t._isDemo)
    if (uncategorised.length === 0) return 0

    setSyncing(true)
    try {
      const results = await autoCategoriseBatch(uncategorised, merchantRules)
      for (const r of results) {
        await categoriseTx(r.id, r.category, r.source || 'auto_ai')
      }
      return results.length
    } finally {
      setSyncing(false)
    }
  }

  // ─────────────────────────────────────────
  // MERCHANT RULES (learned patterns)
  // ─────────────────────────────────────────
  async function loadMerchantRules() {
    const { data } = await supabase
      .from('merchant_rules')
      .select('*')
      .eq('user_id', user.id)

    if (data) {
      const rules = {}
      data.forEach(r => { rules[r.merchant] = r })
      setMerchantRules(rules)
    }
  }

  // ─────────────────────────────────────────
  // PERIOD LOCKING
  // ─────────────────────────────────────────
  function lockPeriod(q)   { setLockedPeriods(prev => [...new Set([...prev, q])]) }
  function unlockPeriod(q) { setLockedPeriods(prev => prev.filter(p => p !== q)) }
  function isPeriodLocked(q) { return lockedPeriods.includes(q) }

  // ─────────────────────────────────────────
  // TAX SNAPSHOT (save point-in-time calculation)
  // ─────────────────────────────────────────
  async function saveTaxSnapshot(snapshot) {
    await supabase.from('tax_snapshots').upsert({
      ...snapshot,
      user_id: user.id,
      snapshotted_at: new Date().toISOString(),
    }, { onConflict: 'user_id,tax_year,quarter' })
  }

  return (
    <AppContext.Provider value={{
      // Data
      transactions, mileageTrips, homeOffice, lockedPeriods,
      categoryHistory, merchantRules,
      // State flags
      loading, syncing, isDemo, coaNudgeDone, setCoaNudgeDone,
      // Transaction actions
      categoriseTx, undoCategorise, addTransaction, uploadReceipt,
      autoCategoriseOnConnect, merchantRules,
      // Mileage actions
      addTrip, removeTrip,
      // Home office
      setHomeOffice,
      // Submissions
      saveSubmission, saveTaxSnapshot,
      // Period locking
      lockPeriod, unlockPeriod, isPeriodLocked,
      // Refresh
      loadAll,
    }}>
      {children}
    </AppContext.Provider>
  )
}

// ─────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────
function getQuarterFromDate(dateStr) {
  const d = new Date(dateStr)
  const m = d.getMonth() + 1 // 1-12
  // UK tax year: Apr-Jun = Q1, Jul-Sep = Q2, Oct-Dec = Q3, Jan-Mar = Q4
  if (m >= 4 && m <= 6)  return 'Q1'
  if (m >= 7 && m <= 9)  return 'Q2'
  if (m >= 10 && m <= 12) return 'Q3'
  return 'Q4'
}

function calcMileageAllowance(miles) {
  if (miles <= 10000) return miles * 0.45
  return 10000 * 0.45 + (miles - 10000) * 0.25
}

export const useAuth = () => useContext(AuthContext)
export const useApp  = () => useContext(AppContext)
